import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { getOrders, getOrdersForUser } from "../../utils/orderStorage";
import { useAuth } from "../../context/AuthContext";

const PAYMENT_LABELS: Record<string, string> = {
  fps: "FPS 轉數快",
  payme: "PayMe",
  card: "信用卡",
};

const Orders = () => {
  const { t } = useTranslation(["common", "checkout"]);
  const { user } = useAuth();
  const orders = useMemo(
    () => (user ? getOrdersForUser(user.id) : getOrders()),
    [user]
  );

  return (
    <Layout title={t("common:my_orders") || "我的訂單"}>
      {orders.length > 0 ? (
        <div className="space-y-4 max-w-3xl">
          {orders.map((order) => (
            <div
              key={order.id}
              className="rounded-2xl bg-space-light/50 border border-white/10 p-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <p className="text-caption text-gray-500">
                    {new Date(order.createdAt).toLocaleString("zh-HK")}
                  </p>
                  <p className="text-h4 text-neon-green font-bold tabular-nums">
                    {order.id}
                  </p>
                </div>
                <span className="inline-flex self-start px-3 py-1 rounded-full text-caption bg-neon-green/10 text-neon-green border border-neon-green/30">
                  {order.status === "processing" ? "處理中" : "已完成"}
                </span>
              </div>

              <ul className="space-y-2 mb-4">
                {order.items.map((item) => (
                  <li
                    key={item.productId}
                    className="flex justify-between text-body-sm text-gray-300"
                  >
                    <span className="line-clamp-1 pr-4">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="text-neon-green tabular-nums shrink-0">
                      HKD {(item.price * item.quantity).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 border-t border-white/10 text-body-sm">
                <span className="text-gray-400">
                  {PAYMENT_LABELS[order.paymentMethod] ?? order.paymentMethod}
                </span>
                <span className="text-white font-bold tabular-nums">
                  HKD {order.subtotal.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="bg-space-light border border-white/10 p-8 rounded-full mb-6">
            <svg
              className="w-12 h-12 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <h3 className="text-h3 mb-2 text-white">
            {t("common:no_orders") || "暫無訂單"}
          </h3>
          <p className="text-body text-gray-400 mb-6">
            {t("common:no_orders_desc")}
          </p>
          <Link to="/products">
            <Button className="bg-neon-green text-space hover:opacity-90">
              {t("common:start_shopping") || "開始購物"}
            </Button>
          </Link>
        </div>
      )}
    </Layout>
  );
};

export default Orders;
